import { gql, useMutation } from "@apollo/client";
import Button from "components/base/Button.jsx";
import File from "components/base/File";
import { FileSelector } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useContext, useMemo } from "react";
import AWSUploader from "../../../components/shared/AWSUploader.js";
import ShipmentContext from "./ShipmentContext";

const ADD_SHIPMENT_ATTACHMENT = gql`
  mutation ADD_SHIPMENT_ATTACHMENT(
    $shipmentId: ID!
    $name: String!
    $url: String!
  ) {
    addShipmentAttachment(shipmentId: $shipmentId, name: $name, url: $url) {
      attachment {
        id
        name
        uri
      }
    }
  }
`;

const REMOVE_SHIPMENT_ATTACHMENT = gql`
  mutation REMOVE_SHIPMENT_ATTACHMENT($id: ID!) {
    removeShipmentAttachment(id: $id) {
      id
    }
  }
`;

function AttachmentFile({ shipmentId, attachment, index, name, src, file }) {
  const { dispatch } = useContext(ShipmentContext);

  const [removeShipmentAttachment, { loading }] = useMutation(
    REMOVE_SHIPMENT_ATTACHMENT,
    {
      onCompleted: () => {
        Alert("success", "Attachment deleted.");
        dispatch({ type: "deleteShipmentAttachments", payload: index });
      },
      onError: (error) => Alert("error", error.message),
    },
  );

  const [createShipmentAttachment] = useMutation(ADD_SHIPMENT_ATTACHMENT, {
    onCompleted: (data) => {
      const attachment = data.addShipmentAttachment.attachment;
      Alert("success", "Attachment added.");
      dispatch({
        type: "addedShipmentAttachment",
        payload: { index, attachment },
      });
    },
    onError: (error) => {
      Alert("error", error.message);
      dispatch({ type: "deleteShipmentAttachments", payload: index });
    },
  });

  return useMemo(
    () => (
      <div className="flex justify-between items-center border-1 border-gray-200 dark:border-gray-700 flex-1 relative border-b">
        {src ? (
          <File name={name} link={src} />
        ) : file ? (
          <div className="relative w-16 h-16">
            <AWSUploader
              className="flex w-full h-full"
              file={file}
              onUploaded={(url) => {
                dispatch({
                  type: "didUploadFileToAws",
                  payload: { index, url },
                });
                createShipmentAttachment({
                  variables: { shipmentId, name, url },
                });
              }}
              onError={(error) => {
                Alert("error", error.message || "Attachment upload failed.");
                dispatch({
                  type: "deleteShipmentAttachments",
                  payload: index,
                });
              }}
            />
          </div>
        ) : null}
        <Button
          danger
          loading={loading}
          onClick={() => {
            if (window.confirm("Are you sure to delete this file?")) {
              if (src) {
                removeShipmentAttachment({
                  variables: { id: attachment.id },
                });
              } else {
                dispatch({
                  type: "deleteShipmentAttachments",
                  payload: index,
                });
              }
            }
          }}
        >
          Delete
        </Button>
      </div>
    ),
    [
      attachment.id,
      createShipmentAttachment,
      dispatch,
      file,
      index,
      loading,
      name,
      removeShipmentAttachment,
      shipmentId,
      src,
    ],
  );
}

function Attachments() {
  const {
    state: { id, attachments },
    dispatch,
  } = useContext(ShipmentContext);

  return useMemo(
    () => (
      <div className="rounded-xl bg-white p-6 dark:bg-gray-900 dark:text-gray-300">
        <h3>Attachments</h3>
        {attachments.length > 0 && (
          <div className="mt-4">
            {attachments.map((attachment, index) => (
              <AttachmentFile
                shipmentId={id}
                attachment={attachment}
                key={index}
                index={index}
                name={attachment.name}
                src={attachment.uri}
                file={attachment.file}
              />
            ))}
          </div>
        )}

        <div className="flex mt-4">
          <FileSelector
            title="Add Attachments"
            onChange={(e) => {
              dispatch({
                type: "onSelectAttachments",
                payload: [...e.target.files].map((file) => ({
                  name: file.name,
                  file,
                })),
              });
              e.target.value = null;
            }}
          />
        </div>
      </div>
    ),
    [id, attachments, dispatch],
  );
}

export default Attachments;
